import { useState, useEffect, useRef, Fragment, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { PLANS } from "@/config/subscriptionPlans";
import { SubscriptionPlan } from "@/server/types/subscription";
import BillingCycleToggle from "./subscription/BillingCycleToggle";
import PlanCard from "./subscription/PlanCard";
import PlanFeatureTable from "./subscription/PlanFeatureTable";
import PlanCheckoutBar from "./subscription/PlanCheckoutBar";
import FreeStrip from "./subscription/FreeStrip";
import MobilePlanSwiper from "./subscription/MobilePlanSwiper";
import PromoStrip from "./subscription/PromoStrip";

const IS_PROMO_ACTIVE = new Date() < new Date("2026-06-21T07:00:00Z");

// Plans shown in the grid/swiper — Free is displayed separately via FreeStrip
const PAID_PLANS = PLANS.filter((p) => p.id !== "free");

export default function SubscriptionPlansView() {
  const { t } = useTranslation();
  const cancelSubscriptionView = useNavigationState(
    (state) => state.cancelSubscriptionView,
  );
  const subscription = useSubscriptionState((state) => state.subscription);
  const currentSubscriptionPlan = subscription.plan;
  const isFree = currentSubscriptionPlan === "free";
  const hasActiveSubscription = subscription.stripe_subscription_id !== null;

  // Trial / add-on state from the /credit-balance DTO. Consumed defensively:
  // the new fields are absent on an un-upgraded BE, and `data` is undefined
  // until the query resolves — FreeStrip/TrialStrip render nothing in that case.
  const { data: creditBalance } = useCreditBalance();

  const [isYearly, setIsYearly] = useState(subscription.interval === "year");
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState<SubscriptionPlan>(isFree ? "proplus" : currentSubscriptionPlan);

  // The trial-ended prompt nudges the user toward the default upsell plan.
  const handleTrialSubscribe = () => setSelectedSubscriptionPlan("proplus");

  // Mirrors TrialStrip's hard-wall condition (enforced && trial ended). When the
  // wall is up a card IS required to continue, so the "no card needed" trust line
  // would contradict it — suppress that one line in this state only.
  // Mirror TrialStrip's SUBSCRIBE-wall condition EXACTLY: exclude `locked` (shows
  // a "paused — contact support" message, not the subscribe wall) and `bypassed`
  // (no wall at all) — otherwise we'd hide the trust line in states where no wall
  // is shown. Optional chaining keeps this aligned with the DTO's nullable shape:
  // a null/absent trial (paid user or old BE) yields `undefined === false` → false,
  // so the wall NEVER engages off a missing trial.
  const trialEndedWallActive =
    creditBalance?.enforced === true &&
    creditBalance?.trial?.active === false &&
    !creditBalance?.trial?.locked &&
    !creditBalance?.trial?.bypassed;

  // Subscribers can't select a different plan from the grid
  const effectiveSelectedSubscriptionPlan = hasActiveSubscription
    ? currentSubscriptionPlan
    : selectedSubscriptionPlan;

  // Sync local state when subscription changes externally (WebSocket update)
  useEffect(() => {
    setSelectedSubscriptionPlan(isFree ? "proplus" : currentSubscriptionPlan);
    setIsYearly(subscription.interval === "year");
  }, [currentSubscriptionPlan, isFree, subscription.interval]);

  // Detect layout breakpoints via container width
  // < 700 → mobile swiper  |  700–1023 → 2-col grid  |  ≥ 1024 → 4-col grid
  const [containerWidth, setContainerWidth] = useState(1200);
  const isCompact = containerWidth < 700;
  const gridCols = containerWidth >= 1024 ? 4 : 2;
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = wrapRef.current?.parentElement ?? wrapRef.current;
    if (!target) {
      return;
    }
    const measure = () =>
      setContainerWidth(target.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  const intervalsMatch =
    (isYearly && subscription.interval === "year") ||
    (!isYearly && subscription.interval === "month");

  const currentPlan =
    PLANS.find((p) => p.id === currentSubscriptionPlan) ?? PLANS[0];
  const selectedPlan =
    PLANS.find((p) => p.id === effectiveSelectedSubscriptionPlan) ??
    PAID_PLANS[0];

  const renewalDate = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  let mobileSavingsSuffix = "";
  if (isYearly) {
    mobileSavingsSuffix = isFree
      ? ` ${t("subscription.yearlySavesUpTo")}`
      : ` ${t("subscription.yearlySavesAmount", { amount: Math.round(currentPlan.annualSavings) })}`;
  }

  let topbarStatus: ReactNode;
  if (isFree) {
    topbarStatus = (
      <>
        {t("subscription.noSubscription")} ·{" "}
        <b className="text-foreground">{t("subscription.freePlanLabel")}</b>
      </>
    );
  } else if (renewalDate) {
    const renewalLabel = subscription.cancel_at_period_end ? (
      t("subscription.expiresOn", { date: renewalDate })
    ) : (
      <>
        {t("subscription.renewsLabel")}{" "}
        <b className="text-foreground">{renewalDate}</b>
      </>
    );
    topbarStatus = (
      <>
        {renewalLabel} · {currentPlan.name}
      </>
    );
  } else {
    topbarStatus = currentPlan.name;
  }

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 flex size-full flex-col overflow-hidden bg-background lg:border-l lg:border-border"
    >
      {/* ── Mobile topbar — sticky ────────────────────────────── */}
      {isCompact && (
        <div className="grid grid-cols-[32px_1fr_32px] items-center gap-2 border-b border-border bg-card/60 px-3.5 py-2.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelSubscriptionView}
            className="size-8"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-center text-[14px] font-semibold">
            {t("subscription.pageTitle")}
          </span>
          <span />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div>
          {/* ── Desktop topbar — scrolls with content ─────────── */}
          {!isCompact && (
            <div className="border-b border-border bg-card/40">
              <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-2">
                  {/* Back control for the 700–959px band, where the view still
                      covers the left column despite the breadcrumb header
                      (docs/settings-middle-views/design.md). Hidden at lg+ —
                      the left column is visible there and back would only
                      produce the empty state. */}
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="back"
                    onClick={cancelSubscriptionView}
                    className="size-8 lg:hidden"
                  >
                    <ChevronLeft size={18} />
                  </Button>
                  <nav className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <span>{t("subscription.settingsBreadcrumb")}</span>
                    <span>/</span>
                    <span className="text-foreground">
                      {t("subscription.pageTitle")}
                    </span>
                  </nav>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {topbarStatus}
                </span>
              </div>
              <div className="mx-auto max-w-2xl px-6 pb-6 pt-2 text-center">
                <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-secondary">
                  {isFree
                    ? t("subscription.choosePlanHeading")
                    : t("subscription.currentPlanHeading", {
                        name: currentPlan.name,
                      })}
                </h1>
                <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
                  {isFree
                    ? t("subscription.choosePlanSubtitle")
                    : t("subscription.currentPlanSubtitle")}
                </p>
                <div className="mt-4 flex justify-center">
                  <BillingCycleToggle
                    isYearly={isYearly}
                    onToggle={setIsYearly}
                    savingsPercent={25}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Mobile hero ───────────────────────────────────── */}
          {isCompact && (
            <div className="px-4 pb-6 pt-9 text-center">
              <h2 className="text-[22px] font-semibold leading-tight tracking-tight text-secondary">
                {isFree
                  ? t("subscription.choosePlanHeading")
                  : t("subscription.currentPlanHeading", {
                      name: currentPlan.name,
                    })}
              </h2>
              <p className="mx-auto mt-1.5 max-w-screen-xs text-[12.5px] leading-relaxed text-muted-foreground">
                {t("subscription.mobilePlanSubtitle")}
                {mobileSavingsSuffix}
              </p>
              <div className="mt-4 flex justify-center">
                <BillingCycleToggle
                  isYearly={isYearly}
                  onToggle={setIsYearly}
                  savingsPercent={25}
                />
              </div>
            </div>
          )}

          {/* ── Promo strip ───────────────────────────────────── */}
          {IS_PROMO_ACTIVE && (
            <PromoStrip
              isYearly={isYearly}
              isCompact={isCompact}
              onSwitchToYearly={() => setIsYearly(true)}
            />
          )}

          {/* ── Plan grid (desktop) / swiper (mobile) ─────────── */}
          {isCompact ? (
            <MobilePlanSwiper
              plans={PAID_PLANS}
              currentPlanId={
                hasActiveSubscription && intervalsMatch
                  ? currentSubscriptionPlan
                  : null
              }
              selectedId={effectiveSelectedSubscriptionPlan}
              onSelectId={setSelectedSubscriptionPlan}
              isYearly={isYearly}
              recommendedId={null}
              isDisabled={hasActiveSubscription}
            />
          ) : (
            <div
              className={`mx-auto grid max-w-[1240px] gap-3.5 px-5 py-8 ${gridCols === 4 ? "grid-cols-4" : "grid-cols-2"}`}
            >
              {PAID_PLANS.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isYearly={isYearly}
                  isSelected={effectiveSelectedSubscriptionPlan === plan.id}
                  isCurrentPlan={
                    currentSubscriptionPlan === plan.id && intervalsMatch
                  }
                  isRecommended={false}
                  onClick={() => setSelectedSubscriptionPlan(plan.id)}
                  isDisabled={hasActiveSubscription}
                />
              ))}
            </div>
          )}

          {/* ── Trial strip (no perpetual free floor) ─────────── */}
          {/* px-5 matches the plan grid + comparison table so the strip's content
              aligns to the same width (without it the strip rendered ~40px wider). */}
          <div
            className={isCompact ? "mx-0 px-5" : "mx-auto max-w-[1240px] px-5"}
          >
            <FreeStrip
              trial={creditBalance?.trial ?? null}
              enforced={creditBalance?.enforced}
              onSubscribe={handleTrialSubscribe}
            />
          </div>

          {/* ── Feature comparison table ──────────────────────── */}
          <div
            className={`px-5 py-8 ${isCompact ? "" : "mx-auto max-w-[1240px]"}`}
          >
            <PlanFeatureTable
              currentPlanId={currentSubscriptionPlan}
              highlightCurrentPlan={!isFree}
            />
          </div>

          {/* ── Trust strip ───────────────────────────────────── */}
          <div className="border-t border-border px-5 py-9">
            <div className="flex flex-col items-center gap-y-1 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-3 sm:gap-y-1">
              {[
                // "no card needed" would contradict the trial-ended wall, where a
                // card IS required to keep chatting — drop it in that state only.
                ...(trialEndedWallActive
                  ? []
                  : [t("subscription.trustNoCard")]),
                t("subscription.trustStripeBilling"),
                t("subscription.trustCreditsReset"),
              ].map((item, i) => (
                <Fragment key={item}>
                  {i > 0 && (
                    <span className="hidden size-1.5 shrink-0 rounded-full bg-secondary/70 sm:block" />
                  )}
                  <span className="text-[11.5px] text-muted-foreground">
                    {item}
                  </span>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky checkout bar ───────────────────────────────── */}
      <div className="shrink-0 border-t border-border bg-card">
        <div className={isCompact ? "" : "mx-auto max-w-[1240px]"}>
          <PlanCheckoutBar
            plan={selectedPlan}
            isYearly={isYearly}
            isCompact={isCompact}
          />
        </div>
      </div>
    </div>
  );
}
